import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ImportedFields,
  RundownImportMode,
  SpreadsheetPreviewResponse,
  SpreadsheetWorksheetMetadata,
} from 'ontime-types';
import { isOntimeGroup, isOntimeMilestone } from 'ontime-types';
import { millisToString, removeTrailingZero } from 'ontime-utils';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { maybeAxiosError } from '../../../../../../common/api/utils';
import { formatDuration } from '../../../../../../common/utils/time';
import {
  type ImportFormValues,
  builtInFieldDefs,
  convertToImportMap,
  getImportWarnings,
  getPersistedImportMode,
  getPersistedImportState,
  getProvidedImportFields,
  getResolvedCustomFields,
  persistImportMode,
  persistImportState,
} from './importMapUtils';
import { deriveHeaderOptionsState } from './spreadsheetImportUtils';

type ImportAction =
  | { type: 'startPreview' }
  | { type: 'startApply' }
  | { type: 'startExport' }
  | { type: 'previewSuccess'; preview: SpreadsheetPreviewResponse }
  | { type: 'applySuccess' }
  | { type: 'exportSuccess' }
  | { type: 'clearPreview'; error?: string; needsRefresh?: boolean }
  | { type: 'failure'; error: string }
  | { type: 'reset' };

type ImportState = {
  loading: '' | 'preview' | 'apply' | 'export';
  error: string;
  preview: SpreadsheetPreviewResponse | null;
  needsPreviewRefresh: boolean;
};

const initialImportState: ImportState = {
  loading: '',
  error: '',
  preview: null,
  needsPreviewRefresh: false,
};

function importReducer(state: ImportState, action: ImportAction): ImportState {
  switch (action.type) {
    case 'startPreview':
      return { ...state, loading: 'preview', error: '' };
    case 'startApply':
      return { ...state, loading: 'apply', error: '' };
    case 'startExport':
      return { ...state, loading: 'export', error: '' };
    case 'previewSuccess':
      return { loading: '', error: '', preview: action.preview, needsPreviewRefresh: false };
    case 'applySuccess':
    case 'exportSuccess':
      return { ...state, loading: '' };
    case 'clearPreview':
      return { ...state, error: action.error ?? '', preview: null, needsPreviewRefresh: action.needsRefresh ?? false };
    case 'failure': {
      if (state.loading === 'preview') {
        return { loading: '', error: action.error, preview: null, needsPreviewRefresh: false };
      }
      return { ...state, loading: '', error: action.error };
    }
    case 'reset':
      return initialImportState;
    default:
      return state;
  }
}

function getPreferredWorksheet(worksheetNames: string[], current: string, fallback: string): string {
  if (worksheetNames.includes(current)) {
    return current;
  }
  if (worksheetNames.includes(fallback)) {
    return fallback;
  }
  return worksheetNames[0] ?? '';
}

const emptyHeaders: string[] = [];

function buildColumnLabels(values: ImportFormValues): string[] {
  const builtIn: string[] = [];
  for (let i = 0; i < values.builtIn.length; i++) {
    if (values.builtIn[i].enabled) {
      builtIn.push(builtInFieldDefs[i].label);
    }
  }

  const custom: string[] = [];
  for (const { ontimeName } of getResolvedCustomFields(values.custom)) {
    if (ontimeName) {
      custom.push(ontimeName);
    }
  }

  return [...builtIn, ...custom];
}

interface UseSheetImportFormProps {
  sourceKey: string;
  defaultRundownName: string;
  worksheetNames: string[];
  initialMetadata: SpreadsheetWorksheetMetadata | null;
  loadMetadata: (worksheet: string) => Promise<SpreadsheetWorksheetMetadata>;
  previewImport: (importMap: ReturnType<typeof convertToImportMap>) => Promise<SpreadsheetPreviewResponse>;
  onApply: (
    preview: SpreadsheetPreviewResponse,
    mode: RundownImportMode,
    newRundownTitle: string,
    providedFields: ImportedFields,
  ) => Promise<void>;
  onExport?: (importMap: ReturnType<typeof convertToImportMap>) => Promise<void>;
}

export function useSheetImportForm({
  sourceKey,
  defaultRundownName,
  worksheetNames,
  initialMetadata,
  loadMetadata,
  previewImport,
  onApply,
  onExport,
}: UseSheetImportFormProps) {
  const { initialFormValues, skipAutoPreview } = useMemo(() => {
    const { values, isPersisted } = getPersistedImportState(sourceKey);
    const worksheet = getPreferredWorksheet(worksheetNames, values.worksheet, initialMetadata?.worksheet ?? '');
    // Skip auto-preview when restoring a persisted mapping that was saved for a different worksheet.
    const hasWorksheetChanged = values.worksheet !== worksheet;
    return {
      initialFormValues: { ...values, worksheet },
      skipAutoPreview: isPersisted && hasWorksheetChanged,
    };
  }, [initialMetadata?.worksheet, sourceKey, worksheetNames]);

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    setValue,
    watch,
    formState: { isValid },
  } = useForm<ImportFormValues>({
    mode: 'onChange',
    defaultValues: initialFormValues,
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'custom' });
  const values = watch();

  // --- Worksheet metadata via react-query ---
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.removeQueries({ queryKey: ['worksheet-metadata', sourceKey] });
    if (initialMetadata) {
      queryClient.setQueryData(['worksheet-metadata', sourceKey, initialMetadata.worksheet], initialMetadata);
    }
  }, [initialMetadata, sourceKey, queryClient]);

  const worksheetMetadataQuery = useQuery({
    queryKey: ['worksheet-metadata', sourceKey, values.worksheet],
    queryFn: () => loadMetadata(values.worksheet),
    enabled: Boolean(values.worksheet) && worksheetNames.includes(values.worksheet),
    staleTime: Infinity,
  });

  const headers = worksheetMetadataQuery.data?.headers ?? emptyHeaders;
  const isLoadingMetadata = worksheetMetadataQuery.isLoading;
  const metadataError = worksheetMetadataQuery.error ? maybeAxiosError(worksheetMetadataQuery.error) : '';

  // --- Derived state ---
  const { sampleHeaders, assignedHeaders } = deriveHeaderOptionsState(values, headers);
  const columnLabels = buildColumnLabels(values);

  const [state, dispatch] = useReducer(importReducer, initialImportState);
  const [importMode, setImportMode] = useState<RundownImportMode>(() => getPersistedImportMode(sourceKey));
  const [newRundownTitle, setNewRundownTitle] = useState(defaultRundownName);
  const warnings = getImportWarnings(values, headers);
  const warningCount = Object.values(warnings).filter(Boolean).length;
  const previewRef = useRef<SpreadsheetPreviewResponse | null>(null);
  const autoPreviewFiredRef = useRef(false);

  // Rehydrate the form from persisted/default state whenever the source context changes.
  useEffect(() => {
    autoPreviewFiredRef.current = false;
    reset(initialFormValues);
    dispatch({ type: 'reset' });
  }, [initialFormValues, reset]);

  // Update import mode and rundown name if the source changes
  useEffect(() => {
    setImportMode(getPersistedImportMode(sourceKey));
    setNewRundownTitle(defaultRundownName);
  }, [sourceKey, defaultRundownName]);

  // Keep the worksheet selection valid if the available worksheets change underneath the form.
  useEffect(() => {
    if (worksheetNames.length === 0) return;
    if (worksheetNames.includes(values.worksheet)) return;
    setValue('worksheet', worksheetNames[0], { shouldDirty: true, shouldValidate: true });
  }, [setValue, values.worksheet, worksheetNames]);

  useEffect(() => {
    previewRef.current = state.preview;
  }, [state.preview]);

  // Clear preview on any form change without re-subscribing on preview updates.
  useEffect(() => {
    const sub = watch(() => {
      if (!previewRef.current) return;
      previewRef.current = null;
      dispatch({ type: 'clearPreview', needsRefresh: true });
    });
    return () => sub.unsubscribe();
  }, [watch]);

  // Race condition guard for async preview requests.
  const requestIdRef = useRef(0);

  const handlePreview = useCallback(
    async (formValues: ImportFormValues) => {
      const importMap = convertToImportMap(formValues);
      const id = ++requestIdRef.current;

      try {
        dispatch({ type: 'startPreview' });
        const previewData = await previewImport(importMap);

        if (requestIdRef.current !== id) return;

        dispatch({ type: 'previewSuccess', preview: previewData });
      } catch (error) {
        if (requestIdRef.current !== id) return;
        dispatch({ type: 'failure', error: maybeAxiosError(error) });
      }
    },
    [previewImport],
  );

  // Auto-preview on mount once metadata is ready.
  useEffect(() => {
    if (autoPreviewFiredRef.current) return;
    if (skipAutoPreview) return;
    if (!isValid || headers.length === 0) return;

    autoPreviewFiredRef.current = true;
    handleSubmit(handlePreview)();
  }, [skipAutoPreview, isValid, headers, handleSubmit, handlePreview]);

  const handleApply = useCallback(async () => {
    if (!state.preview) return;

    try {
      dispatch({ type: 'startApply' });
      const providedFields = getProvidedImportFields(convertToImportMap(getValues()));
      await onApply(state.preview, importMode, newRundownTitle, providedFields);
      persistImportState(sourceKey, getValues());
      persistImportMode(sourceKey, importMode);
      dispatch({ type: 'applySuccess' });
    } catch (error) {
      dispatch({ type: 'failure', error: maybeAxiosError(error) });
    }
  }, [getValues, importMode, newRundownTitle, onApply, sourceKey, state.preview]);

  const handleExport = useCallback(
    async (formValues: ImportFormValues) => {
      if (!onExport) return;
      try {
        dispatch({ type: 'startExport' });
        const importMap = convertToImportMap(formValues);
        await onExport(importMap);
        dispatch({ type: 'exportSuccess' });
      } catch (error) {
        dispatch({ type: 'failure', error: maybeAxiosError(error) });
      }
    },
    [onExport],
  );

  const isBusy = Boolean(state.loading);
  const canPreview = isValid && !isLoadingMetadata && !isBusy && worksheetNames.length > 0;
  const displayError = metadataError || state.error;
  const addCustomField = useCallback(() => {
    append({ importName: '', ontimeName: '' });
  }, [append]);

  const toolbarStatus = (() => {
    if (!state.preview) {
      return {
        entries: '–',
        groups: '–',
        milestones: '–',
        start: '–',
        end: '–',
        duration: '–',
        warnings: warningCount,
      };
    }

    const { entries, flatOrder } = state.preview.rundown;
    const { start, end, duration } = state.preview.summary;
    let groups = 0;
    let milestones = 0;
    for (const entryId of flatOrder) {
      const entry = entries[entryId];
      if (isOntimeGroup(entry)) groups++;
      else if (isOntimeMilestone(entry)) milestones++;
    }

    return {
      entries: flatOrder.length,
      groups,
      milestones,
      start: removeTrailingZero(millisToString(start)),
      end: removeTrailingZero(millisToString(end)),
      duration: formatDuration(duration),
      warnings: warningCount,
    };
  })();

  return {
    values,
    setValue,
    fields,
    addCustomField,
    removeCustomField: remove,
    sampleHeaders,
    assignedHeaders,
    warnings,
    columnLabels,
    worksheetHeaders: headers,
    state,
    toolbarStatus,
    isLoadingMetadata,
    isBusy,
    canPreview,
    displayError,
    importMode,
    setImportMode,
    newRundownTitle,
    setNewRundownTitle,
    handlePreviewSubmit: handleSubmit(handlePreview),
    handleExportSubmit: handleSubmit(handleExport),
    handleApply,
  };
}
